import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ChevronDown, ChevronRight, ClipboardCheck, GripVertical, X, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PageHeader, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import { ImageUpload } from "@/pages/admin/AdminUtils";
import { toast } from "sonner";

const emptyLesson = { title: "", description: "", published: true, content_blocks: [], links: [], resources: [] };

function LessonDialog({ phaseId, lesson, onClose, onSaved }) {
  const [form, setForm] = useState(lesson || emptyLesson);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const blocks = form.content_blocks || [];
  const setBlock = (i, k, v) => { const b = [...blocks]; b[i] = { ...b[i], [k]: v }; set("content_blocks", b); };
  const addBlock = (type) => set("content_blocks", [...blocks, { type, value: "", label: "" }]);
  const moveBlock = (i, d) => { const b = [...blocks]; const j = i + d; if (j < 0 || j >= b.length) return; [b[i], b[j]] = [b[j], b[i]]; set("content_blocks", b); };
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (form.id) await api.put(`/admin/lessons/${form.id}`, form);
    else await api.post(`/admin/phases/${phaseId}/lessons`, form);
    toast.success("Lesson saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Lesson</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="lesson-title" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="lesson-desc" /></div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Content Blocks</Label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => addBlock("richtext")} data-testid="add-block-text">+ Text</Button>
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => addBlock("image")} data-testid="add-block-image">+ Image</Button>
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => addBlock("link")} data-testid="add-block-link">+ Link</Button>
              </div>
            </div>
            <div className="space-y-2">
              {blocks.map((b, i) => (
                <div key={i} className="p-3 rounded-xl bg-secondary/40 border border-border" data-testid={`block-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase text-teal">{b.type}</span>
                    <div className="flex gap-1">
                      <button onClick={() => moveBlock(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveBlock(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => set("content_blocks", blocks.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-red-500" /></button>
                    </div>
                  </div>
                  {b.type === "richtext" && <RichTextEditor value={b.value} onChange={(html) => setBlock(i, "value", html)} testId={`rich-block-${i}`} />}
                  {b.type === "text" && <Textarea placeholder="Paragraph text" value={b.value} onChange={(e) => setBlock(i, "value", e.target.value)} />}
                  {b.type === "image" && <ImageUpload label="Upload image" value={b.value} onChange={(v) => setBlock(i, "value", v)} testId={`img-block-${i}`} />}
                  {b.type === "link" && <div className="space-y-1"><Input placeholder="Label" value={b.label} onChange={(e) => setBlock(i, "label", e.target.value)} /><Input placeholder="URL" value={b.value} onChange={(e) => setBlock(i, "value", e.target.value)} /></div>}
                </div>
              ))}
              {blocks.length === 0 && <p className="text-xs text-muted-foreground">No content blocks yet.</p>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="lesson-published" /> Published</label>
        </div>
        <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-lesson">Save Lesson</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssessmentDialog({ phaseId, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState({ question: "", options: ["", "", "", ""], correct_index: 0, published: true });
  const load = async () => setData((await api.get(`/phases/${phaseId}/assessment`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phaseId]);
  const save = async () => {
    if (!q.question || q.options.filter(Boolean).length < 2) return toast.error("Question and at least 2 options required");
    const payload = { ...q, options: q.options.filter(Boolean) };
    if (q.id) await api.put(`/admin/questions/${q.id}`, payload); else await api.post(`/admin/phases/${phaseId}/questions`, payload);
    setQ({ question: "", options: ["", "", "", ""], correct_index: 0, published: true }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/questions/${id}`); load(); };
  const setPass = async (v) => { await api.put(`/admin/phases/${phaseId}/assessment`, { pass_score: parseInt(v) || 70 }); load(); };
  if (!data) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Assessment Questions</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Label className="whitespace-nowrap">Pass score (%)</Label>
          <Input type="number" className="w-24" defaultValue={data.assessment.pass_score} onBlur={(e) => setPass(e.target.value)} data-testid="pass-score" />
        </div>
        <div className="space-y-2 mb-4">
          {data.questions.map((question, i) => (
            <div key={question.id} className="p-3 rounded-xl bg-secondary/40 flex items-start justify-between gap-2" data-testid={`adm-q-${i}`}>
              <div className="text-sm">
                <p className="font-medium">{i + 1}. {question.question}</p>
                <ul className="mt-1 space-y-0.5">{question.options.map((o, oi) => <li key={oi} className={oi === question.correct_index ? "text-brandgreen" : "text-muted-foreground"}>{oi === question.correct_index ? "✓ " : "• "}{o}</li>)}</ul>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => setQ({ ...question, options: [...question.options, ...Array(Math.max(0, 4 - question.options.length)).fill("")] })}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(question.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
          {data.questions.length === 0 && <p className="text-sm text-muted-foreground">No questions yet.</p>}
        </div>
        <div className="border-t border-border pt-3 space-y-2">
          <p className="font-medium text-sm">{q.id ? "Edit" : "Add"} question</p>
          <Textarea placeholder="Question" value={q.question} onChange={(e) => setQ({ ...q, question: e.target.value })} data-testid="q-text" />
          {q.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" checked={q.correct_index === i} onChange={() => setQ({ ...q, correct_index: i })} data-testid={`q-correct-${i}`} />
              <Input placeholder={`Option ${i + 1}`} value={o} onChange={(e) => { const op = [...q.options]; op[i] = e.target.value; setQ({ ...q, options: op }); }} data-testid={`q-opt-${i}`} />
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setQ({ ...q, options: [...q.options, ""] })}>+ Option</Button>
            <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-question">{q.id ? "Update" : "Add"} Question</Button>
          </div>
          <p className="text-xs text-muted-foreground">Select the radio next to the correct answer. Correct answers are never revealed to students.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCourses() {
  const [courses, setCourses] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [phases, setPhases] = useState([]);
  const [openPhase, setOpenPhase] = useState(null);
  const [lessons, setLessons] = useState({});
  const [lessonDialog, setLessonDialog] = useState(null); // {phaseId, lesson}
  const [assessPhase, setAssessPhase] = useState(null);
  const [phaseEdit, setPhaseEdit] = useState(null);

  useEffect(() => { api.get("/admin/courses").then((r) => { setCourses(r.data); const main = r.data.find((c) => c.type === "main") || r.data[0]; if (main) setCourseId(main.id); }); }, []);
  const loadPhases = async (cid) => setPhases((await api.get(`/courses/${cid}/phases`)).data);
  useEffect(() => { if (courseId) loadPhases(courseId); }, [courseId]);

  const loadLessons = async (pid) => setLessons((m) => ({ ...m, [pid]: [] })) || api.get(`/phases/${pid}/lessons`).then((r) => setLessons((m) => ({ ...m, [pid]: r.data })));
  const togglePhase = (pid) => { setOpenPhase((o) => (o === pid ? null : pid)); if (!lessons[pid]) loadLessons(pid); };

  const addPhase = async () => { await api.post(`/admin/courses/${courseId}/phases`, {}); loadPhases(courseId); };
  const savePhase = async () => { await api.put(`/admin/phases/${phaseEdit.id}`, phaseEdit); setPhaseEdit(null); loadPhases(courseId); toast.success("Phase updated"); };
  const delPhase = async (id) => { if (!window.confirm("Delete this phase and its lessons?")) return; await api.delete(`/admin/phases/${id}`); loadPhases(courseId); };
  const delLesson = async (pid, id) => { await api.delete(`/admin/lessons/${id}`); loadLessons(pid); };

  if (!courses) return <Loader />;

  return (
    <div>
      <PageHeader title="Course CMS" subtitle="Manage phases, lessons, content, and assessments"
        action={
          <Select value={courseId || ""} onValueChange={setCourseId}>
            <SelectTrigger className="w-64" data-testid="course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
            <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} {c.type === "main" ? "(Main)" : ""}</SelectItem>)}</SelectContent>
          </Select>} />

      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={addPhase} data-testid="add-phase"><Plus className="w-4 h-4 mr-1.5" /> Add Phase</Button></div>

      <div className="space-y-3">
        {phases.map((p, idx) => (
          <div key={p.id} className="bg-white border border-border rounded-2xl shadow-sm overflow-hidden" data-testid={`adm-phase-${idx}`}>
            <div className="flex items-center gap-3 p-4">
              <button onClick={() => togglePhase(p.id)} className="flex items-center gap-2 flex-1 text-left" data-testid={`adm-phase-toggle-${idx}`}>
                {openPhase === p.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                <span className="font-heading font-semibold">{p.title}</span>
                {!p.published && <span className="text-xs text-muted-foreground">(unpublished)</span>}
              </button>
              <Button size="sm" variant="ghost" onClick={() => setPhaseEdit(p)} data-testid={`edit-phase-${idx}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => delPhase(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {openPhase === p.id && (
              <div className="border-t border-border p-4 space-y-2">
                {(lessons[p.id] || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-secondary/40" data-testid={`adm-lesson-${l.id}`}>
                    <span className="text-sm">{l.title} {!l.published && <span className="text-xs text-muted-foreground">(draft)</span>}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setLessonDialog({ phaseId: p.id, lesson: l })}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => delLesson(p.id, l.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setLessonDialog({ phaseId: p.id, lesson: null })} data-testid={`add-lesson-${idx}`}><Plus className="w-4 h-4 mr-1" /> Add Lesson</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAssessPhase(p.id)} data-testid={`manage-assessment-${idx}`}><ClipboardCheck className="w-4 h-4 mr-1" /> Assessment</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {phases.length === 0 && <p className="text-muted-foreground text-sm">No phases yet. Add one to get started.</p>}
      </div>

      {lessonDialog && <LessonDialog phaseId={lessonDialog.phaseId} lesson={lessonDialog.lesson} onClose={() => setLessonDialog(null)} onSaved={() => { loadLessons(lessonDialog.phaseId); setLessonDialog(null); }} />}
      {assessPhase && <AssessmentDialog phaseId={assessPhase} onClose={() => setAssessPhase(null)} />}

      <Dialog open={!!phaseEdit} onOpenChange={(o) => !o && setPhaseEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Phase</DialogTitle></DialogHeader>
          {phaseEdit && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={phaseEdit.title} onChange={(e) => setPhaseEdit({ ...phaseEdit, title: e.target.value })} data-testid="phase-title" /></div>
              <div><Label>Description</Label><Textarea value={phaseEdit.description || ""} onChange={(e) => setPhaseEdit({ ...phaseEdit, description: e.target.value })} data-testid="phase-desc" /></div>
              <div><Label>Title (Tagalog)</Label><Input value={phaseEdit.title_tl || ""} onChange={(e) => setPhaseEdit({ ...phaseEdit, title_tl: e.target.value })} data-testid="phase-title-tl" /></div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={phaseEdit.requires_assessment} onCheckedChange={(v) => setPhaseEdit({ ...phaseEdit, requires_assessment: v })} data-testid="phase-req-assess" /> Requires assessment</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={phaseEdit.requires_evidence} onCheckedChange={(v) => setPhaseEdit({ ...phaseEdit, requires_evidence: v })} data-testid="phase-req-evidence" /> Requires evidence submission</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={phaseEdit.published} onCheckedChange={(v) => setPhaseEdit({ ...phaseEdit, published: v })} data-testid="phase-published" /> Published</label>
            </div>
          )}
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={savePhase} data-testid="save-phase">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
